import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, Download, ShieldCheck } from "lucide-react";

const SPECIALTIES = [
  { id: "all", label: "All specialties" },
  { id: "cardiology", label: "Cardiology" },
  { id: "pediatrics", label: "Pediatrics" },
  { id: "oncology", label: "Oncology" },
  { id: "neurology", label: "Neurology" },
  { id: "primary_care", label: "Primary Care" },
  { id: "radiology", label: "Radiology" },
  { id: "pathology", label: "Pathology" },
  { id: "genomics", label: "Genomics" },
  { id: "dermatology", label: "Dermatology" },
  { id: "ophthalmology", label: "Ophthalmology" },
  { id: "surgery", label: "Surgery" },
  { id: "anesthesiology", label: "Anesthesiology" },
  { id: "nuclear_medicine", label: "Nuclear Medicine" },
];

export default function Marketplace() {
  const [apps, setApps] = useState([]);
  const [specialty, setSpecialty] = useState("all");

  useEffect(() => {
    http.get(`/marketplace?specialty=${specialty}`).then((r) => setApps(r.data.apps));
  }, [specialty]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16" data-testid="marketplace-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-800">Marketplace</p>
          <h1 className="mt-2 font-display text-3xl lg:text-5xl font-bold text-slate-900">
            Apps built by the healthcare community.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Discover, fork and install healthcare apps published by doctors and developers.
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="marketplace-filters">
        {SPECIALTIES.map((s) => (
          <button
            key={s.id}
            data-testid={`marketplace-filter-${s.id}`}
            onClick={() => setSpecialty(s.id)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              specialty === s.id
                ? "bg-emerald-900 text-white border-emerald-900"
                : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"
            }`}
          >{s.label}</button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="marketplace-grid">
        {apps.map((app) => (
          <div key={app.id} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-400 hover:shadow-md transition-all fade-in" data-testid={`marketplace-card-${app.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-mono uppercase text-emerald-700 tracking-wide">{app.specialty?.replace('_', ' ')}</span>
              <div className="flex items-center gap-1 text-xs text-sky-700"><ShieldCheck className="h-3.5 w-3.5" />Verified</div>
            </div>
            <h3 className="mt-3 font-display font-semibold text-xl text-slate-900">{app.title}</h3>
            <p className="mt-1 text-sm text-slate-600">{app.tagline}</p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {(app.tags || []).map((t) => (
                <Badge key={t} variant="secondary" className="bg-slate-100 text-slate-700 font-normal">{t}</Badge>
              ))}
            </div>
            <div className="mt-5 flex items-center justify-between text-sm">
              <div className="text-slate-600">
                <span className="text-slate-400">by</span> {app.creator_name}
                <span className="ml-2 text-[10px] font-mono uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">{app.creator_role}</span>
              </div>
              <div className="flex items-center gap-1 text-slate-500 text-xs">
                <Heart className="h-3.5 w-3.5" /> {app.installs}
              </div>
            </div>
            <Button className="mt-4 w-full bg-emerald-900 hover:bg-emerald-800 text-white" data-testid={`btn-install-${app.id}`}>
              <Download className="h-4 w-4 mr-2" /> Install
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
